/**
 * Identity hooks back the Users admin surface: the member list, admin user
 * creation, role changes, and per-user agent grants.
 *
 * The list is gated by `gatewayOnline` (the runtime owns the source of
 * truth); grant mutations re-fetch instead of optimistic-updating because
 * revocation must be observably synchronous.
 */

import { hubQueryKeys, type UserCreatePayload, type UserRole } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useUsersQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.users(),
    queryFn: () => api.users.list(),
    enabled: options.enabled ?? true,
  });
}

export function useUserQuery(userId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.user(userId),
    queryFn: () => api.users.get(userId),
    enabled: (options.enabled ?? true) && Boolean(userId),
  });
}

export function useCreateUserMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserCreatePayload) => api.users.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.users() });
    },
  });
}

export function useRemoveUserMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.users.remove(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.users() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.user(userId) });
    },
  });
}

export function useSetUserRoleMutation(userId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: UserRole) => api.users.setRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.users() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.user(userId) });
    },
  });
}

export function useUpdateUserDisplayNameMutation(userId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.users.updateDisplayName(userId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.users() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.user(userId) });
    },
  });
}

export function useUserGrantsQuery(userId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.userGrants(userId),
    queryFn: () => api.users.grants.list(userId),
    enabled: (options.enabled ?? true) && Boolean(userId),
  });
}
